/**
 * 模型加载器：manifest → IndexedDB 命中检查 → 带进度下载 → SHA-256 校验 → 写缓存
 *
 * 不变量：
 * - 缓存键 = manifest 条目 id；sha256 不一致即视为未命中（版本升级自然失效）
 * - 校验失败的下载结果绝不落盘
 * - IndexedDB 不可用时抛出 ModelCacheError（由上层降级为每次下载并提示）
 */
import type { ModelEntry } from "./manifest";

const DB_NAME = "grip-models";
const DB_VERSION = 1;
const STORE = "models";

export interface CachedModel {
  id: string;
  sha256: string;
  bytes: number;
  cachedAt: string;
  data: ArrayBuffer;
}

export interface DownloadProgress {
  id: string;
  loaded: number;
  total: number;
  /** 0..1；total 未知时为 NaN */
  ratio: number;
  phase: "downloading" | "verifying" | "storing" | "done";
}

export class ModelCacheError extends Error {}
export class ModelDownloadError extends Error {}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new ModelCacheError("IndexedDB 不可用"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () =>
      reject(new ModelCacheError(`IndexedDB 打开失败：${req.error?.message}`));
  });
}

function idbGet(db: IDBDatabase, id: string): Promise<CachedModel | undefined> {
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, "readonly").objectStore(STORE).get(id);
    req.onsuccess = () => resolve(req.result as CachedModel | undefined);
    req.onerror = () =>
      reject(new ModelCacheError(`IndexedDB 读取失败：${req.error?.message}`));
  });
}

function idbPut(db: IDBDatabase, value: CachedModel): Promise<void> {
  return new Promise((resolve, reject) => {
    const req = db
      .transaction(STORE, "readwrite")
      .objectStore(STORE)
      .put(value);
    req.onsuccess = () => resolve();
    req.onerror = () =>
      reject(new ModelCacheError(`IndexedDB 写入失败：${req.error?.message}`));
  });
}

export async function getCachedModel(id: string): Promise<CachedModel | undefined> {
  const db = await openDb();
  try {
    return await idbGet(db, id);
  } finally {
    db.close();
  }
}

/** 列出全部缓存条目的元信息（不含 data，供 UI 展示） */
export async function listCached(): Promise<
  Array<Omit<CachedModel, "data">>
> {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const req = db.transaction(STORE, "readonly").objectStore(STORE).getAll();
      req.onsuccess = () =>
        resolve(
          (req.result as CachedModel[]).map(({ id, sha256, bytes, cachedAt }) => ({
            id,
            sha256,
            bytes,
            cachedAt,
          })),
        );
      req.onerror = () =>
        reject(new ModelCacheError(`IndexedDB 列举失败：${req.error?.message}`));
    });
  } finally {
    db.close();
  }
}

export async function removeCached(id: string): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const req = db.transaction(STORE, "readwrite").objectStore(STORE).delete(id);
      req.onsuccess = () => resolve();
      req.onerror = () =>
        reject(new ModelCacheError(`IndexedDB 删除失败：${req.error?.message}`));
    });
  } finally {
    db.close();
  }
}

async function sha256Hex(buf: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * 流式下载（带进度回调）。要求响应支持流式读取；Content-Length 缺失时
 * total 取 manifest 声明值。
 */
async function downloadWithProgress(
  url: string,
  expectedBytes: number,
  onProgress?: (p: DownloadProgress) => void,
  id = url,
): Promise<ArrayBuffer> {
  const resp = await fetch(url);
  if (!resp.ok) {
    throw new ModelDownloadError(`下载失败：HTTP ${resp.status} ${url}`);
  }
  const total =
    Number(resp.headers.get("Content-Length")) > 0
      ? Number(resp.headers.get("Content-Length"))
      : expectedBytes;

  if (!resp.body) {
    // 极端环境无 ReadableStream：整包读取，无细粒度进度
    const buf = await resp.arrayBuffer();
    onProgress?.({ id, loaded: buf.byteLength, total, ratio: 1, phase: "downloading" });
    return buf;
  }

  const reader = resp.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    onProgress?.({
      id,
      loaded,
      total,
      ratio: total > 0 ? loaded / total : NaN,
      phase: "downloading",
    });
  }
  const out = new Uint8Array(loaded);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.byteLength;
  }
  return out.buffer as ArrayBuffer;
}

/**
 * 确保模型可用：缓存命中（sha256 匹配 manifest）直接返回；
 * 否则下载 → SHA-256 校验 → 写 IndexedDB → 返回。
 * URL 解析：entry.url 绝对地址优先；否则 manifest.base + entry.file。
 */
export async function ensureModel(
  id: string,
  entry: ModelEntry,
  opts: {
    /** manifest.base（模型直链前缀） */
    base?: string;
    /** manifest.mirrors（备用前缀，主源失败按序回退） */
    mirrors?: string[];
    onProgress?: (p: DownloadProgress) => void;
    /** 强制重新下载（忽略缓存） */
    force?: boolean;
  } = {},
): Promise<ArrayBuffer> {
  const report = opts.onProgress;

  if (!opts.force) {
    try {
      const cached = await getCachedModel(id);
      if (cached && cached.sha256 === entry.sha256 && cached.bytes === entry.bytes) {
        report?.({ id, loaded: entry.bytes, total: entry.bytes, ratio: 1, phase: "done" });
        return cached.data;
      }
    } catch (e) {
      if (!(e instanceof ModelCacheError)) throw e;
      // 缓存不可用：继续走下载（不落盘）
    }
  }

  const url = entry.file.startsWith("https://")
    ? entry.file
    : `${opts.base ?? "/models/"}${entry.file}`;
  // T10 双源回退：主源失败按序尝试镜像前缀（文件名不变，SHA-256 校验兜底一致性）
  const mirrorUrls = (opts.mirrors ?? []).map((m) => `${m}${entry.file}`);
  let buf: ArrayBuffer | null = null;
  let lastErr: unknown = null;
  for (const u of [url, ...mirrorUrls]) {
    try {
      buf = await downloadWithProgress(u, entry.bytes, report, id);
      break;
    } catch (e) {
      lastErr = e;
      report?.({ id, loaded: 0, total: entry.bytes, ratio: 0, phase: "downloading" });
    }
  }
  if (!buf) throw lastErr ?? new ModelDownloadError(`下载失败：${id}`);

  report?.({ id, loaded: entry.bytes, total: entry.bytes, ratio: 1, phase: "verifying" });
  const digest = await sha256Hex(buf);
  if (digest !== entry.sha256) {
    throw new ModelDownloadError(
      `SHA-256 校验失败：${id}（期望 ${entry.sha256.slice(0, 12)}…，实际 ${digest.slice(0, 12)}…）`,
    );
  }
  if (buf.byteLength !== entry.bytes) {
    throw new ModelDownloadError(
      `大小不符：${id}（期望 ${entry.bytes}，实际 ${buf.byteLength}）`,
    );
  }

  try {
    report?.({ id, loaded: entry.bytes, total: entry.bytes, ratio: 1, phase: "storing" });
    const db = await openDb();
    try {
      await idbPut(db, {
        id,
        sha256: entry.sha256,
        bytes: entry.bytes,
        cachedAt: new Date().toISOString(),
        data: buf,
      });
    } finally {
      db.close();
    }
  } catch (e) {
    if (!(e instanceof ModelCacheError)) throw e;
    // 缓存写失败不致命：本次仍可用，下次重新下载
  }

  report?.({ id, loaded: entry.bytes, total: entry.bytes, ratio: 1, phase: "done" });
  return buf;
}
